import { AuthScreen, LinkAccountForm } from '@h2hspark/auth-ui';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthBrandMark } from '../auth-brand-mark';

/**
 * Reached from the social-callback route when a Google sign-in's email matches an account
 * that already exists. The member proves ownership, the two sign-in methods are joined, and
 * they continue into the app.
 *
 * The chrome is the sign-in screen's (2026-09-12). Both states, including the dead-link one:
 * a member who lands here from a spent link was one press away from signing in, and a bare
 * sentence on a grey page is the least reassuring thing this product can show at that moment.
 * It now says the same thing inside the same card the rest of the journey uses, with a route
 * out that is a control rather than a word in a paragraph.
 */
export function MemberLinkAccountPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow') ?? '';

  if (flowId.length === 0) {
    return (
      <AuthScreen
        chrome="brand"
        mark={<AuthBrandMark />}
        title="This link has expired"
        description="Links that join a Google sign-in to an existing account are single-use and short-lived."
      >
        <p className="text-sm text-muted-foreground">
          Start again from sign-in and choose <span className="font-semibold">Continue with
          Google</span>. If the account already exists, you will be brought back here with a fresh
          link.
        </p>
        {/* `.auth-ui-brand .auth-ui-button` already gives this the pill and the full width, so
            the link wears the same shape as every other primary action on these screens without
            ceasing to be a link — it navigates, and it should behave like it. */}
        <Link to="/app/login" className="auth-ui-button mt-5 no-underline">
          Back to sign in
        </Link>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      chrome="brand"
      mark={<AuthBrandMark />}
      title="Link sign-in method"
      description="Use your existing password or get a one-time code by email."
    >
      <LinkAccountForm
        flowId={flowId}
        onSuccess={() => navigate('/app', { replace: true })}
        onNotLinkable={() => navigate('/app/login', { replace: true })}
      />
    </AuthScreen>
  );
}
